import { api } from 'api/axios';
import { AxiosResponse } from 'axios';
import { getAppointmentStatus } from 'components/appointments/helpers';
import dayjs from 'dayjs';
import {
  AppointmentStatus,
  IAppointment,
  IAppointmentPage,
  PaymentIntent,
} from 'model/appointment';
import { useMutation, useQuery } from 'react-query';
import { AuthService } from 'utils/auth';
import { CONFIG, DEFAULT_REFRESH_INTERVAL } from 'utils/config';
import { queryClient } from 'utils/reactQueryClient';
import {
  deviceConfigByType,
  useFamilies,
  useMedicalcareDevices,
  useMedicineDeliveryDevices,
  usePatientInfo,
} from './usePatient';
import { useTranslation } from './useTranslation';
import { useAuth } from 'utils/AuthProvider';
import { createDeviceWithConfig, ensureDeviceExists } from './deviceUtils';
import { useMedicalView } from '../utils/MedicalViewProvider';
import notificationAudio from '../asset/audios/notification.mp3';

export enum AppointmentType {
  medicalcare = 'medicalcare',
  medicine_delivery = 'medicine_delivery',
}

export interface AppointmentFilter {
  request?: boolean;
  canceled?: boolean;
  confirmCancel?: boolean;
  appointmentType?: AppointmentType;
  from?: string;
  to?: string;
}

export async function getAppointments(
  filter: AppointmentFilter,
  patientInfoId?: string,
): Promise<IAppointmentPage> {
  const queryString = Object.entries(filter)
    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
    .join('&');
  const response = await api.get(
    `/medicalcare/appointments?role=PATIENT&page=0&pageSize=99999999&${queryString}${patientInfoId ? `&patientInfoId=${patientInfoId}` : ''}`,
  );
  return response.data;
}
export async function getDoctorAppointments(
  filter: AppointmentFilter,
): Promise<IAppointmentPage> {
  const queryString = Object.entries(filter)
    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
    .join('&');
  const response = await api.get(
    `/medicalcare/appointments?role=DOCTOR&page=0&pageSize=99999999&${queryString}`,
  );
  return response.data;
}

export async function listAppointmentRequests(data: {
  appointmentType: AppointmentType;
}) {
  const res = await api.get(
    `/medicalcare/appointments?role=DOCTOR&page=0&pageSize=99999999&appointmentType=${data.appointmentType}&request=true&confirmCancel=false`,
  );
  return res.data;
}
export async function listAppointmentConfirmCancel(data: {
  appointmentType: AppointmentType;
}) {
  const res = await api.get(
    `/medicalcare/appointments?role=DOCTOR&page=0&pageSize=99999999&appointmentType=${data.appointmentType}&canceled=true&confirmCancel=false`,
  );
  return res.data;
}
export async function listAppointmentConfirmPaid(data: {
  appointmentType: AppointmentType;
}) {
  const res = await api.get(
    `/medicalcare/appointments?role=DOCTOR&page=0&pageSize=99999999&appointmentType=${data.appointmentType}&confirmPaid=false`,
  );
  return res.data;
}

export async function getAppointment(
  appointmentId?: string,
): Promise<IAppointment> {
  const response = await api.get(`/medicalcare/appointments/${appointmentId}`);
  return response.data;
}

export async function getAppointmentVideoToken(
  appointmentId?: string,
): Promise<AxiosResponse<{ token: string; uid: string }>> {
  const response = await api.post(
    `/medicalcare/appointments/${appointmentId}/fetch-rtctoken`,
    {},
  );
  return response.data;
}

export const useAppointments = (filter: AppointmentFilter) => {
  const { defaultPatientInfoId } = useAuth();
  const { data, ...rest } = useQuery<unknown, unknown, IAppointmentPage>(
    ['appointments', filter, defaultPatientInfoId],
    () => getAppointments(filter, defaultPatientInfoId),
    {
      refetchOnWindowFocus: true,
      refetchOnMount: true,
      refetchOnReconnect: true,
      refetchIntervalInBackground: true,
      refetchInterval: DEFAULT_REFRESH_INTERVAL,
    },
  );

  const appointmentsByTime = data?.items.slice().reverse() || [];

  const currentAppointments: IAppointment[] = [];
  const pastAppointments: IAppointment[] = [];

  appointmentsByTime.forEach((appointment) => {
    const status = getAppointmentStatus(appointment);
    const now = dayjs().startOf('day');
    const startedAt = dayjs(appointment.startedAt);
    switch (status) {
      case AppointmentStatus.PENDING:
      case AppointmentStatus.IN_PROGRESS:
      case AppointmentStatus.PAYMENT_PENDING:
      case AppointmentStatus.CONFIRMED:
        currentAppointments.push(appointment);
        break;
      case AppointmentStatus.PENDING_EXPIRED:
      case AppointmentStatus.EXPIRED:
      case AppointmentStatus.CANCELED:
        pastAppointments.push(appointment);
        break;
      case AppointmentStatus.PAYMENT_COMPLETED:
        if (startedAt.isAfter(now)) {
          currentAppointments.push(appointment);
        } else {
          pastAppointments.push(appointment);
        }
        break;
      default:
        pastAppointments.push(appointment);
    }
  });
  currentAppointments.sort((a, b) =>
    dayjs(a.startedAt || a.timeForSorting).diff(
      dayjs(b.startedAt || b.timeForSorting),
    ),
  );

  return {
    appointments: appointmentsByTime,
    currentAppointments: currentAppointments,
    pastAppointments: pastAppointments,
    ...rest,
  };
};

export const useAppointment = (appointmentId?: string) => {
  const { data, ...rest } = useQuery<unknown, unknown, IAppointment>(
    ['appointment', appointmentId],
    () => getAppointment(appointmentId),
    {
      enabled: !!appointmentId,
      staleTime: 0,
      cacheTime: 0,
    },
  );
  return { appointment: data, ...rest };
};

export const useAppointmentVideoToken = (appointmentId?: string) => {
  const { data, ...rest } = useQuery<
    unknown,
    unknown,
    { token: string; uid: string }
  >(
    ['appointmentVideoToken', appointmentId],
    () => getAppointmentVideoToken(appointmentId),
    {
      enabled: !!appointmentId,
    },
  );
  return { token: data?.token, uid: data?.uid, ...rest };
};

export async function createAppointment(data: Partial<IAppointment>) {
  const response = await api.post('/medicalcare/appointments', data);
  return response.data;
}

export async function updateAppointment(
  appointmentId: string,
  data: Partial<IAppointment>,
) {
  const response = await api.put(
    `/medicalcare/appointments/${appointmentId}`,
    data,
  );
  return response.data;
}
export async function cancelAppointment(appointmentId: string) {
  const response = await api.put(`/medicalcare/appointments/${appointmentId}`, {
    canceled: true,
  });
  return response.data;
}

export const useCancelAppointment = () => {
  const { mutateAsync, ...rest } = useMutation<unknown, unknown, string>(
    (appointmentId) => cancelAppointment(appointmentId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['appointments']);
        queryClient.invalidateQueries(['appointment']);
      },
    },
  );
  return { cancelAppointment: mutateAsync, ...rest };
};

export async function enterAppointment(
  appointmentId: string,
  remoteEntered: boolean = false,
) {
  const response = await api.post(
    `/medicalcare/appointments/${appointmentId}/enter`,
    {
      remoteEntered,
    },
  );
  return response.data;
}

export const useCreateAppointment = () => {
  const { t } = useTranslation();
  const { patientInfo } = usePatientInfo();
  const { families } = useFamilies();
  const { medicalcareDevices } = useMedicalcareDevices();
  const { medicineDeliveryDevices } = useMedicineDeliveryDevices();

  const { mutateAsync, ...rest } = useMutation<
    unknown,
    unknown,
    Partial<IAppointment>
  >(
    async (data) => {
      const appointmentType = data.appointmentType;
      if (!appointmentType) {
        throw new Error('Appointment type is required');
      }

      // Get device configuration for the appointment type
      const config = deviceConfigByType(appointmentType, t);

      // Get the appropriate devices based on appointment type
      const currentTypeDevices =
        appointmentType === AppointmentType.medicalcare
          ? medicalcareDevices
          : medicineDeliveryDevices;

      const otherTypeDevices =
        appointmentType === AppointmentType.medicalcare
          ? medicineDeliveryDevices
          : medicalcareDevices;
      // check if current device exists, if not create it, if yes, just create appointment directly
      if (currentTypeDevices && currentTypeDevices.length === 0) {
        // check if other type device exists
        if (otherTypeDevices && otherTypeDevices.length === 0) {
          // create the device in the first family
          await ensureDeviceExists(
            currentTypeDevices,
            config,
            families,
            patientInfo || {},
          );
        } else {
          const familyId = otherTypeDevices?.[0]?.familyId;
          // create the device in the same family as the other type device
          if (familyId) {
            await createDeviceWithConfig(config, familyId, patientInfo);
          } else {
            // create the device in the first family
            await ensureDeviceExists(
              currentTypeDevices,
              config,
              families,
              patientInfo || {},
            );
          }
        }
      }

      // Create appointment
      return createAppointment(data);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['appointments']);
      },
    },
  );
  return { createAppointment: mutateAsync, ...rest };
};

export async function createPaymentIntent(
  appointmentId: string,
  paymethod: string,
): Promise<PaymentIntent> {
  const response = await api.post(
    `/medicalcare/appointments/${appointmentId}/paymentIntent`,
    {
      paymethod,
    },
  );
  return response.data as PaymentIntent;
}

export function getPaypayStartURL(
  paymentIntentId: string,
  cardInfoString: string,
  callbackUrl: string,
): string {
  const token = AuthService.ljToken();
  const host = CONFIG.apiUrl;
  return `${host}/pay/paypay/start?target=${callbackUrl}&token=${token}&paymentIntentId=${paymentIntentId}&info=${cardInfoString}`;
}

export async function stripePaymentCheckout(
  appointmentId: string,
): Promise<{ checkoutUrl: string }> {
  const response = await api.post(
    `/medicalcare/appointments/${appointmentId}/checkout`,
    {},
  );
  return response.data;
}

export const useAppointmentsForCalendarView = (
  appointmentType: AppointmentType,
) => {
  const { selectedDate } = useMedicalView();
  const from = dayjs(selectedDate)
    .startOf('month')
    .startOf('date')
    .toISOString();
  const to = dayjs(selectedDate).endOf('month').endOf('date').toISOString();
  const filter = {
    appointmentType,
    from,
    to,
  };
  const { data, ...rest } = useQuery<unknown, unknown, IAppointmentPage>(
    ['appointmentsForCalendarView', filter],
    () => getDoctorAppointments(filter),
    {
      refetchOnWindowFocus: true,
      refetchOnMount: true,
      refetchOnReconnect: true,
      refetchIntervalInBackground: true,
      refetchInterval: DEFAULT_REFRESH_INTERVAL,
    },
  );
  const appointments = (data?.items || []).filter(
    (a) => !a.canceled || (a.canceled && a.confirmCancel),
  );
  return { appointments, ...rest };
};

function playNotificationAudio() {
  const audio = new Audio(notificationAudio);
  audio.play();
}

function hasAddedRequestingAppointments(
  previousAppointments: IAppointment[] | undefined,
  nextAppointments: IAppointment[],
): boolean {
  if (!previousAppointments) return nextAppointments.length > 0;

  const previousIds = new Set(
    previousAppointments.map((appointment) => appointment.id),
  );
  return nextAppointments.some(
    (appointment) => !previousIds.has(appointment.id),
  );
}

export const useRequestingAppointments = (appointmentType: AppointmentType) => {
  const { data, ...rest } = useQuery<unknown, unknown, IAppointment[]>(
    ['requestingAppointments', appointmentType],
    async () => {
      const [requestingData, pendingConfirmCancelData, pendingConfirmPaidData] =
        await Promise.all([
          listAppointmentRequests({ appointmentType }),
          listAppointmentConfirmCancel({ appointmentType }),
          listAppointmentConfirmPaid({ appointmentType }),
        ]);

      const requestingAppointments = requestingData.items || [];
      const pendingConfirmCancelAppointments =
        pendingConfirmCancelData.items || [];
      const pendingConfirmPaidAppointments = pendingConfirmPaidData.items || [];
      const pendingConfirmPaidAppointmentIds = new Set(
        pendingConfirmPaidAppointments.map(
          (appointment: IAppointment) => appointment.id,
        ),
      );
      const pendingConfirmCancelAppointmentIds = new Set(
        pendingConfirmCancelAppointments.map(
          (appointment: IAppointment) => appointment.id,
        ),
      );

      const filteredRequestingAppointments = requestingAppointments
        .filter(
          (appointment: IAppointment) =>
            !pendingConfirmCancelAppointmentIds.has(appointment.id),
        )
        .filter(
          (appointment: IAppointment) =>
            !pendingConfirmPaidAppointmentIds.has(appointment.id),
        );

      const nextAppointments = [
        ...filteredRequestingAppointments,
        ...pendingConfirmCancelAppointments,
        ...pendingConfirmPaidAppointments,
      ].sort((a, b) => dayjs(a.createdAt).diff(dayjs(b.createdAt)));

      const previousAppointments = queryClient.getQueryData<IAppointment[]>([
        'requestingAppointments',
        appointmentType,
      ]);
      if (
        hasAddedRequestingAppointments(previousAppointments, nextAppointments)
      ) {
        playNotificationAudio();
      }

      return nextAppointments;
    },
    {
      refetchOnWindowFocus: true,
      refetchOnMount: true,
      refetchOnReconnect: true,
      refetchIntervalInBackground: true,
      refetchInterval: DEFAULT_REFRESH_INTERVAL,
    },
  );
  return { requestingAppointments: data || [], ...rest };
};
